import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db } from './firebase';
import { collection, query, onSnapshot, doc, updateDoc, increment, addDoc, serverTimestamp, getDocs, where, runTransaction } from 'firebase/firestore';
import { deleteToRecycleBin } from './recycleBinUtil';
import { isCourierCod, courierStatusText } from './dueUtil';
import { printSalesInvoice } from './printInvoice';

const getTodayString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTk = (num) => new Intl.NumberFormat('en-IN').format(Math.round(num || 0));

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = dateStr?.toDate ? dateStr.toDate() : new Date(dateStr);
  if (isNaN(d.getTime())) return 'N/A';
  const day = String(d.getDate()).padStart(2, '0');
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${day}-${months[d.getMonth()]}-${d.getFullYear()}, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const getSaleDateKey = (sale) => {
  const raw = sale.createdAt;
  const d = raw?.toDate ? raw.toDate() : new Date(raw);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const Due = () => {
  const [salesList, setSalesList] = useState([]);
  const [startDate, setStartDate] = useState(getTodayString());
  const [endDate, setEndDate] = useState(getTodayString());
  const [activeRange, setActiveRange] = useState('today');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewInvoice, setViewInvoice] = useState(null);
  const [receiveInput, setReceiveInput] = useState('');
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showDuplicateReview, setShowDuplicateReview] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [duplicateCandidates, setDuplicateCandidates] = useState([]);
  const [scanningDuplicates, setScanningDuplicates] = useState(false);
  const [hasScanned, setHasScanned] = useState(false);
  // 'customer' = কাস্টমার নিজে বাকি রেখেছে | 'cod' = টাকাটা কুরিয়ারের কাছে আছে
  const [view, setView] = useState('customer');
  // ---- ডাবল-ক্লিক গার্ড: Sales.js-এ একই ধরনের বাগ (ডাবল-ক্লিকে সেল দুইবার সেভ হওয়া) আগে
  // ফিক্স হয়েছিল isSubmittingRef দিয়ে -- এখানেও একই প্যাটার্ন, কারণ "Receive" বাটনে দ্রুত
  // দুইবার ক্লিক করলে (বা দুই জায়গা থেকে প্রায় একই সময়ে সেটল করলে) আগে পেমেন্ট দুইবার
  // রেকর্ড হয়ে যেতে পারতো এবং dueAmount নেগেটিভ হয়ে যেতে পারতো।
  const isReceivingRef = useRef(false);
  const [receiving, setReceiving] = useState(false);

  useEffect(() => {
    // পুরো sales history না টেনে শুধু যাদের বাকি টাকা আছে সেগুলোই সরাসরি Firestore থেকে আনা হচ্ছে —
    // sales collection যত বড় হোক না কেন, due তালিকা সবসময় ছোট এবং দ্রুত থাকবে।
    const unsub = onSnapshot(query(collection(db, "sales"), where("dueAmount", ">", 0)), (snap) => {
      setSalesList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const dueInRange = useMemo(() => {
    return salesList.filter(s => {
      const key = getSaleDateKey(s);
      if (!key) return false;
      return key >= startDate && key <= endDate;
    }).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  }, [salesList, startDate, endDate]);

  // ডুপ্লিকেট-স্ক্যান এখন আর অটোমেটিক চলে না (এতে প্রতিবার পেজ খুললেই পুরো sales history
  // পড়তে হতো) — মূল ডাবল-সেল বাগ আগেই ফিক্স হয়ে গেছে, তাই এখন এটা শুধু পুরনো ডেটা
  // মাঝেমধ্যে চেক করার জন্য একটা ম্যানুয়াল বাটন।
  const scanForDuplicates = async () => {
    setScanningDuplicates(true);
    try {
      const snap = await getDocs(collection(db, "sales"));
      setDuplicateCandidates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setHasScanned(true);
    } catch (e) {
      alert("Scan failed: " + e.message);
    } finally {
      setScanningDuplicates(false);
    }
  };

  // ---- কাস্টমার-ডিউ আর কুরিয়ার-COD আলাদা করা ----
  // দুটোই "বাকি টাকা", কিন্তু টাকাটা দুই জনের কাছে: একটা কাস্টমারের কাছে (আদায় করতে হবে),
  // আরেকটা পার্সেলের সাথে কুরিয়ারের কাছে (ডেলিভারি হলে Pathao পাঠাবে)। একসাথে দেখালে
  // "কে কত টাকা বাকি রেখেছে" ভুল আসে, তাই আলাদা দুটো ট্যাব।
  const customerDueInRange = useMemo(() => dueInRange.filter(s => !isCourierCod(s)), [dueInRange]);

  // COD-এর ক্ষেত্রে তারিখ ফিল্টার করা হয় না — প্রশ্নটাই হলো "কুরিয়ারে আমার মোট কত টাকা
  // আটকে আছে", সেটা কোন দিনের সেল তার উপর নির্ভর করে না।
  const codInTransit = useMemo(
    () => salesList.filter(isCourierCod).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)),
    [salesList]
  );

  const activeList = view === 'cod' ? codInTransit : customerDueInRange;

  const searchedDue = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return activeList;
    return activeList.filter(s =>
      (s.name || '').toLowerCase().includes(term) ||
      (s.phone || '').toLowerCase().includes(term) ||
      (s.phone2 || '').toLowerCase().includes(term) ||
      (s.invoiceId || '').toLowerCase().includes(term)
    );
  }, [activeList, searchTerm]);

  const totalDue = useMemo(() => searchedDue.reduce((sum, s) => sum + Number(s.dueAmount || 0), 0), [searchedDue]);
  const totalCod = useMemo(() => codInTransit.reduce((sum, s) => sum + Number(s.dueAmount || 0), 0), [codInTransit]);
  const isCodView = view === 'cod';

  // ---- একই invoiceId-তে একাধিক সেল ডকুমেন্ট থাকলে সেগুলো ডুপ্লিকেট বাগের ফলাফল ----
  // (আগে একই সাবমিশন একাধিকবার Firestore-এ সেভ হয়ে যেত)। এখানে গ্রুপ করে
  // সবচেয়ে পুরনোটা রেখে বাকিগুলো রিভিউ করার জন্য দেখানো হচ্ছে।
  const duplicateGroups = useMemo(() => {
    const map = new Map();
    duplicateCandidates.forEach(s => {
      if (!s.invoiceId) return;
      if (!map.has(s.invoiceId)) map.set(s.invoiceId, []);
      map.get(s.invoiceId).push(s);
    });
    return Array.from(map.values())
      .filter(group => group.length > 1)
      .map(group => {
        const sorted = [...group].sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
        return { keep: sorted[0], duplicates: sorted.slice(1) };
      });
  }, [duplicateCandidates]);

  const totalDuplicateCount = duplicateGroups.reduce((sum, g) => sum + g.duplicates.length, 0);

  const cleanupDuplicates = async () => {
    setCleaning(true);
    let deletedCount = 0;
    let skippedPaid = 0;
    const restockFailed = [];
    try {
      for (const group of duplicateGroups) {
        for (const dup of group.duplicates) {
          // ইতিমধ্যে যে ডুপ্লিকেটে পেমেন্ট রিসিভ করা হয়েছে, সেটা নিরাপত্তার জন্য বাদ দেওয়া হচ্ছে —
          // ম্যানুয়ালি রিভিউ করা দরকার, স্বয়ংক্রিয়ভাবে মুছে ফেলা ঠিক হবে না।
          if (Number(dup.paid || 0) > 0) { skippedPaid++; continue; }
          for (const item of (dup.items || [])) {
            try {
              await updateDoc(doc(db, "products", item.id), { stock: increment(item.qty) });
            } catch (e) {
              restockFailed.push(`${item.name || item.id} (Invoice #${dup.invoiceId})`);
            }
          }
          await deleteToRecycleBin({ collectionName: 'sales', id: dup.id, data: dup, label: 'Sale', reason: 'Removed as duplicate invoice' });
          deletedCount++;
        }
      }
      let msg = `${deletedCount} duplicate sale(s) deleted and stock restored.`;
      if (skippedPaid > 0) msg += `\n\n${skippedPaid} skipped because they already had a payment received — please check manually.`;
      if (restockFailed.length > 0) msg += `\n\nCould not restore stock for these items (product may have been deleted): ${restockFailed.join(', ')}`;
      alert(msg);
      setShowDuplicateReview(false);
      setDuplicateCandidates([]);
      setHasScanned(false);
    } catch (e) {
      alert("Cleanup failed: " + e.message);
    } finally {
      setCleaning(false);
    }
  };

  const setQuickRange = (mode) => {
    const today = new Date();
    if (mode === 'today') {
      const t = getTodayString();
      setStartDate(t); setEndDate(t);
    } else if (mode === 'yesterday') {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      const ys = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, '0')}-${String(y.getDate()).padStart(2, '0')}`;
      setStartDate(ys); setEndDate(ys);
    } else if (mode === 'all') {
      setStartDate('2000-01-01'); setEndDate(getTodayString());
    }
    setActiveRange(mode);
  };

  const loadPaymentHistory = async (saleId) => {
    try {
      const snap = await getDocs(query(collection(db, "duePayments"), where("saleId", "==", saleId)));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
      setPaymentHistory(list);
    } catch (e) {
      setPaymentHistory([]);
    }
  };

  const openInvoiceView = (sale) => {
    setViewInvoice(sale);
    setReceiveInput(String(Number(sale.dueAmount || 0)));
    setPaymentHistory([]);
    loadPaymentHistory(sale.id);
  };

  const submitReceive = async () => {
    if (!viewInvoice) return;
    if (isReceivingRef.current) return; // আগেরটা এখনো প্রসেস হচ্ছে -- নতুন ক্লিক গ্রহণ করা হবে না
    const requestedAmt = Math.max(0, Number(receiveInput) || 0);
    if (requestedAmt <= 0) return alert("Invalid amount!");

    isReceivingRef.current = true;
    setReceiving(true);
    let appliedAmt = 0;
    try {
      // ---- আগে এখানে viewInvoice.dueAmount (মডাল খোলার সময়কার পুরনো/stale ভ্যালু) দিয়ে
      // amt ক্ল্যাম্প করে সরাসরি increment() পাঠানো হতো -- এতে ডাবল-ক্লিক বা অন্য কোথাও
      // থেকে একই বকেয়া প্রায় একই সময়ে সেটল হলে পেমেন্ট দুইবার রেকর্ড হয়ে dueAmount
      // নেগেটিভ হয়ে যেতে পারতো (আর তখন সেটা Due লিস্ট থেকেই উধাও হয়ে যেত, কারণ লিস্টটা
      // dueAmount > 0 দিয়ে ফিল্টার করা)। এখন runTransaction দিয়ে পেমেন্ট নেওয়ার ঠিক
      // মুহূর্তে সার্ভার থেকে আসল dueAmount পড়ে তার সাথেই ক্ল্যাম্প করা হচ্ছে। ----
      await runTransaction(db, async (transaction) => {
        const saleRef = doc(db, "sales", viewInvoice.id);
        const saleSnap = await transaction.get(saleRef);
        const freshDue = saleSnap.exists() ? Number(saleSnap.data().dueAmount || 0) : 0;
        if (freshDue <= 0) { appliedAmt = 0; return; }
        appliedAmt = Math.min(freshDue, requestedAmt);
        transaction.update(saleRef, { paid: increment(appliedAmt), dueAmount: increment(-appliedAmt) });
      });

      if (appliedAmt <= 0) {
        alert("This due has already been settled (maybe from another device/tab).");
        return;
      }

      await addDoc(collection(db, "duePayments"), {
        invoiceId: viewInvoice.invoiceId,
        saleId: viewInvoice.id,
        customerName: viewInvoice.name || '',
        customerPhone: [viewInvoice.phone, viewInvoice.phone2].filter(Boolean).join(', ') || '',
        amount: appliedAmt,
        createdAt: serverTimestamp()
      });
      const due = Number(viewInvoice.dueAmount || 0);
      const updated = { ...viewInvoice, paid: (Number(viewInvoice.paid) || 0) + appliedAmt, dueAmount: Math.max(0, due - appliedAmt) };
      setViewInvoice(updated);
      setReceiveInput(String(updated.dueAmount));
      setPaymentHistory(prev => [...prev, { amount: appliedAmt, createdAt: new Date() }]);
      alert(`৳ ${formatTk(appliedAmt)} received!`);
    } catch (e) {
      alert("Error receiving payment: " + e.message);
    } finally {
      isReceivingRef.current = false;
      setReceiving(false);
    }
  };

  return (
    <div style={{ padding: '20px', background: '#f4f7fe', minHeight: '100vh' }}>
      <div className="white-box" style={{ padding: '30px', borderRadius: '25px', background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
          <h2 style={{ margin: 0, color: '#333' }}>💰 Due Management</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* COD তালিকায় তারিখ ফিল্টার লুকানো — ওটা সবসময় সব সময়ের, কারণ প্রশ্নটা
                "আজকে কত" নয়, "কুরিয়ারে এখন মোট কত টাকা আটকে আছে" */}
            {!isCodView && (
              <>
                {[{ key: 'today', label: 'Today' }, { key: 'yesterday', label: 'Yesterday' }, { key: 'all', label: 'All Time' }].map(r => (
                  <button
                    key={r.key}
                    onClick={() => setQuickRange(r.key)}
                    style={{
                      padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
                      border: `1px solid ${activeRange === r.key ? '#4a00e0' : '#ddd'}`,
                      background: activeRange === r.key ? '#4a00e0' : '#fff',
                      color: activeRange === r.key ? '#fff' : '#333'
                    }}
                  >
                    {r.label}
                  </button>
                ))}
                <input type="date" className="input-field" value={startDate} onChange={e => { setStartDate(e.target.value); setActiveRange(null); }} style={{ width: '150px', marginBottom: 0 }} />
                <input type="date" className="input-field" value={endDate} onChange={e => { setEndDate(e.target.value); setActiveRange(null); }} style={{ width: '150px', marginBottom: 0 }} />
              </>
            )}
            <button onClick={scanForDuplicates} disabled={scanningDuplicates} style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
              {scanningDuplicates ? 'Scanning...' : '🔍 Scan for Duplicate Sales'}
            </button>
          </div>
        </div>

        {/* ---- বাকি টাকা কার কাছে: কাস্টমার নাকি কুরিয়ার ---- */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[
            { key: 'customer', label: `👤 Customer Due (${customerDueInRange.length})`, color: '#f59e0b' },
            { key: 'cod', label: `🚚 Courier COD (${codInTransit.length})`, color: '#3b82f6' }
          ].map(t => (
            <button
              key={t.key}
              onClick={() => { setView(t.key); setSearchTerm(''); }}
              style={{
                padding: '9px 18px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
                border: `1px solid ${view === t.key ? t.color : '#ddd'}`,
                background: view === t.key ? t.color : '#fff',
                color: view === t.key ? '#fff' : '#666'
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {hasScanned && !scanningDuplicates && duplicateGroups.length === 0 && (
          <div style={{ background: '#f0fff4', border: '1px solid #b3e6c8', borderRadius: '15px', padding: '15px 20px', marginBottom: '20px', fontSize: '13px', color: '#218c61' }}>
            ✅ No duplicate sales found.
          </div>
        )}

        {duplicateGroups.length > 0 && (
          <div style={{ background: '#fff5f5', border: '1px solid #ffb3b3', borderRadius: '15px', padding: '15px 20px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <b style={{ color: '#c0392b' }}>⚠️ Duplicate sales found!</b>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
                Found {totalDuplicateCount} duplicate record(s) across {duplicateGroups.length} invoice(s). These happened because the same sale was saved more than once, and stock was wrongly deducted for each duplicate too.
              </p>
            </div>
            <button onClick={() => setShowDuplicateReview(true)} style={{ padding: '10px 20px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' }}>Review & Clean Up</button>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px', flexWrap: 'wrap', marginBottom: '25px' }}>
          <div className="hover-lift" style={{
            background: '#fff', padding: '20px', borderRadius: '20px',
            borderLeft: `6px solid ${isCodView ? '#3b82f6' : '#f59e0b'}`, boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
            display: 'flex', alignItems: 'center', gap: '15px', width: 'fit-content', minWidth: '280px'
          }}>
            <div style={{ fontSize: '30px' }}>{isCodView ? '🚚' : '👤'}</div>
            <div>
              <p style={{ margin: 0, color: '#666', fontSize: '14px', fontWeight: 'bold' }}>
                {isCodView ? 'COD IN COURIER (all time)' : 'TOTAL DUE (this period)'}
              </p>
              <h2 style={{ margin: 0, color: isCodView ? '#3b82f6' : '#f59e0b', fontSize: '24px' }}>৳ {formatTk(isCodView ? totalCod : totalDue)}</h2>
              {!isCodView && totalCod > 0 && (
                <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#3b82f6', fontWeight: 'bold' }}>
                  + ৳ {formatTk(totalCod)} কুরিয়ারে আটকে আছে — পাশের ট্যাবে
                </p>
              )}
            </div>
          </div>

          <input
            type="text"
            placeholder="🔍 Search by Name / Phone / Invoice..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="input-field"
            style={{ width: '300px', marginBottom: 0 }}
          />
        </div>

        {isCodView && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '15px', padding: '14px 20px', marginBottom: '20px', fontSize: '13px', color: '#1e40af', lineHeight: 1.7 }}>
            এই টাকাগুলো কাস্টমার বাকি রাখেনি — পার্সেলের সাথে <b>কুরিয়ারের কাছে</b> আছে। ডেলিভারি হলে Pathao এই টাকা আপনাকে পাঠাবে।<br />
            পেমেন্ট হাতে পাওয়ার পর <b>View Invoice → Receive</b> চাপলে সেই ইনভয়েসটা এই তালিকা থেকে চলে যাবে এবং হিসাব মিলে যাবে।
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
              <th style={{ padding: '15px' }}>DATE</th>
              <th>INVOICE ID</th>
              <th>CUSTOMER</th>
              <th>TOTAL BILL</th>
              <th>{isCodView ? 'ADVANCE' : 'PAID'}</th>
              {isCodView && <th>COURIER STATUS</th>}
              <th>{isCodView ? 'COD' : 'DUE'}</th>
              <th>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {searchedDue.map(sale => (
              <tr key={sale.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                <td style={{ padding: '15px', fontSize: '13px' }}>{formatDateTime(sale.createdAt)}</td>
                <td><b style={{ color: '#4a00e0' }}>#{sale.invoiceId}</b></td>
                <td>
                  {sale.name || "Walk-In"}
                  <div style={{ fontSize: '11px', color: '#999' }}>{[sale.phone, sale.phone2].filter(Boolean).join(', ')}</div>
                </td>
                <td style={{ fontWeight: 'bold' }}>৳ {formatTk(sale.totalBill)}</td>
                <td style={{ color: '#10b981', fontWeight: 'bold' }}>৳ {formatTk(sale.paid)}</td>
                {isCodView && (
                  <td style={{ fontSize: '12px', color: sale.courierBooking?.consignmentId ? '#1e40af' : '#c0392b', fontWeight: 'bold' }}>
                    {courierStatusText(sale)}
                  </td>
                )}
                <td>
                  <span style={{
                    padding: '4px 10px', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px',
                    background: isCodView ? '#eff6ff' : '#fce8e6',
                    color: isCodView ? '#1e40af' : '#c5221f'
                  }}>
                    ৳ {formatTk(sale.dueAmount)}
                  </span>
                </td>
                <td>
                  <button onClick={() => openInvoiceView(sale)} style={{ padding: '6px 15px', background: '#4a00e0', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>View Invoice</button>
                </td>
              </tr>
            ))}
            {searchedDue.length === 0 && (
              <tr><td colSpan={isCodView ? 8 : 7} style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
                {isCodView ? 'কুরিয়ারে কোনো টাকা আটকে নেই — ৳ 0' : 'No due amount in this period — ৳ 0'}
              </td></tr>
            )}
          </tbody>
          {searchedDue.length > 0 && (
            <tfoot>
              <tr style={{ borderTop: '2px solid #f4f7fe', fontWeight: 'bold', background: '#f8f9ff' }}>
                <td style={{ padding: '12px 15px' }} colSpan={isCodView ? 6 : 5}>TOTAL ({searchedDue.length})</td>
                <td style={{ color: isCodView ? '#1e40af' : '#c5221f' }}>৳ {formatTk(totalDue)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {viewInvoice && (
        <div onClick={() => setViewInvoice(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', padding: '30px', borderRadius: '25px', width: '450px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative', maxHeight: '95vh', overflowY: 'auto' }}>
            <button onClick={() => setViewInvoice(null)} style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: '#ffeded', color: 'red', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer', fontWeight: 'bold', zIndex: 10 }}>X</button>

            <div style={{ textAlign: 'center', marginBottom: '15px' }}>
              <h1 style={{ margin: 0, color: '#4a00e0', fontSize: '22px' }}>PB KIZUNA COMPANY LIMITED</h1>
              <p style={{ margin: '4px 0', fontSize: '11px', fontWeight: 'bold' }}>H-10, R-2, Section-2, Block-D, <br />Aftabnagar, Dhaka-1212</p>
              <p style={{ margin: 0, fontSize: '11px' }}>Mobile: 01884-711844</p>
              <div style={{ borderBottom: '1px dashed #000', margin: '12px 0' }}></div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', letterSpacing: '1px' }}>SALES INVOICE</h4>
            </div>

            <div style={{ display: 'flex', gap: '10px', fontSize: '12px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <p style={{ margin: '2px 0' }}><b>Sold By:</b> {viewInvoice.soldBy}</p>
                <p style={{ margin: '2px 0' }}><b>Date:</b> {viewInvoice.dateTime || formatDateTime(viewInvoice.createdAt)}</p>
                <p style={{ margin: '2px 0' }}><b>Invoice ID:</b> #{viewInvoice.invoiceId}</p>
              </div>
              <div style={{ flex: 1, border: '1px solid #ddd', borderRadius: '8px', padding: '8px 10px', background: '#f8f9ff' }}>
                <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', color: '#4a00e0' }}>Bill To</p>
                <p style={{ margin: '2px 0' }}>{viewInvoice.name || 'Walk-in Customer'}</p>
                {[viewInvoice.phone, viewInvoice.phone2].filter(Boolean).length > 0 && (
                  <p style={{ margin: '2px 0' }}>{[viewInvoice.phone, viewInvoice.phone2].filter(Boolean).join(', ')}</p>
                )}
                {viewInvoice.address && <p style={{ margin: '2px 0' }}>{viewInvoice.address}</p>}
              </div>
            </div>
            <div style={{ borderBottom: '1px dashed #000', margin: '8px 0 12px' }}></div>

            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  <th align='left' style={{ paddingBottom: '4px' }}>ITEM</th>
                  <th align='center' style={{ paddingBottom: '4px' }}>QTY</th>
                  <th align='right' style={{ paddingBottom: '4px' }}>TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {(viewInvoice.items || []).map((it, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{ padding: '5px 0' }}>
                      <b>{it.name}</b>
                      {it.itemDiscAmount > 0 && (
                        <div style={{ fontSize: '10px', color: '#e74c3c' }}>Disc: -৳ {formatTk(it.itemDiscAmount)}</div>
                      )}
                    </td>
                    <td align='center'>{it.qty}</td>
                    <td align='right'>{formatTk(it.finalItemTotal ?? (it.price * it.qty))}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ borderTop: '1px solid #eee', marginTop: '10px', paddingTop: '8px', textAlign: 'right', fontSize: '12px' }}>
              {viewInvoice.subtotal > 0 && <p style={{ margin: '3px 0' }}>Sub Total: ৳ {formatTk(viewInvoice.subtotal)}</p>}
              {viewInvoice.totalDiscount > 0 && (
                <p style={{ margin: '3px 0', color: '#e74c3c' }}>Total Discount: -৳ {formatTk(viewInvoice.totalDiscount)}</p>
              )}
              {viewInvoice.deliveryCharge > 0 && (
                viewInvoice.deliveryPaidBy === 'shop' ? (
                  <p style={{ margin: '3px 0', color: '#c0392b' }}>Delivery Charge: ৳ {formatTk(viewInvoice.deliveryCharge)} (paid by {viewInvoice.deliveryPaidByName})</p>
                ) : (
                  <p style={{ margin: '3px 0' }}>Delivery Charge: +৳ {formatTk(viewInvoice.deliveryCharge)}</p>
                )
              )}
              <h3 style={{ margin: '6px 0', fontSize: '14px' }}>Grand Total: ৳ {formatTk(viewInvoice.totalBill)}</h3>
              <p style={{ margin: '3px 0' }}>{isCourierCod(viewInvoice) ? 'Advance Paid' : 'Paid'}: ৳ {formatTk(viewInvoice.paid)}</p>
              {Number(viewInvoice.dueAmount || 0) > 0 ? (
                isCourierCod(viewInvoice) ? (
                  <p style={{ margin: '3px 0', color: '#1e40af', fontWeight: 'bold' }}>
                    Cash on Delivery: ৳ {formatTk(viewInvoice.dueAmount)}
                    <span style={{ display: 'block', fontSize: '11px', fontWeight: 'normal', color: '#666' }}>
                      Courier: {courierStatusText(viewInvoice)}
                    </span>
                  </p>
                ) : (
                  <p style={{ margin: '3px 0', color: '#e74c3c', fontWeight: 'bold' }}>Due: ৳ {formatTk(viewInvoice.dueAmount)}</p>
                )
              ) : (
                <p style={{ margin: '3px 0', color: '#10b981', fontWeight: 'bold' }}>Fully Paid</p>
              )}
            </div>

            <button
              onClick={() => printSalesInvoice(viewInvoice)}
              className="btn-confirm"
              style={{ width: '100%', marginTop: '15px', padding: '10px', fontSize: '13px' }}
            >
              Print Invoice 🖨️
            </button>

            {paymentHistory.length > 0 && (
              <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed #000' }}>
                <p style={{ margin: '0 0 6px 0', fontWeight: 'bold', fontSize: '12px', color: '#4a00e0' }}>Payment History</p>
                {paymentHistory.map((p, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#555', marginBottom: '3px' }}>
                    <span>{formatDateTime(p.createdAt)}</span>
                    <span style={{ color: '#10b981', fontWeight: 'bold' }}>+৳ {formatTk(p.amount)}</span>
                  </div>
                ))}
              </div>
            )}

            {Number(viewInvoice.dueAmount || 0) > 0 && (
              <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px dashed #000', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontWeight: 'bold', fontSize: '13px' }}>{isCourierCod(viewInvoice) ? 'Receive from courier:' : 'Receive:'}</span>
                <input
                  type="number"
                  value={receiveInput}
                  onFocus={e => e.target.select()}
                  onChange={e => setReceiveInput(e.target.value)}
                  style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '13px' }}
                />
                <button onClick={submitReceive} disabled={receiving} style={{ padding: '8px 16px', background: '#4a00e0', color: '#fff', border: 'none', borderRadius: '8px', cursor: receiving ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '13px', opacity: receiving ? 0.7 : 1 }}>{receiving ? 'Receiving...' : 'Receive'}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {showDuplicateReview && (
        <div onClick={() => !cleaning && setShowDuplicateReview(false)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px', padding: '25px', width: '600px', maxWidth: '95%', maxHeight: '85vh', overflowY: 'auto' }}>
            <h3 style={{ marginTop: 0, color: '#c0392b' }}>⚠️ Duplicate Sales Review</h3>
            <p style={{ fontSize: '13px', color: '#666' }}>
              For each invoice below, the oldest copy will be kept and the remaining duplicates will be deleted, with the stock for those items credited back. Any duplicate that already has a payment received against it will be skipped for safety — please check those manually.
            </p>
            {duplicateGroups.map((g, i) => (
              <div key={i} style={{ border: '1px solid #eee', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
                <b style={{ color: '#4a00e0' }}>#{g.keep.invoiceId}</b> — {g.keep.name || 'Walk-in'} — ৳ {formatTk(g.keep.totalBill)}
                <div style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>✓ Keep 1 (earliest: {formatDateTime(g.keep.createdAt)})</div>
                <div style={{ fontSize: '12px', color: '#e74c3c', marginTop: '2px' }}>
                  ✕ Delete {g.duplicates.length} duplicate(s)
                  {g.duplicates.some(d => Number(d.paid || 0) > 0) ? ' — ⚠️ some have payments received, will be skipped' : ''}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <button onClick={() => setShowDuplicateReview(false)} disabled={cleaning} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #ddd', background: '#fff', cursor: cleaning ? 'not-allowed' : 'pointer' }}>Cancel</button>
              <button onClick={cleanupDuplicates} disabled={cleaning} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#e74c3c', color: '#fff', fontWeight: 'bold', cursor: cleaning ? 'not-allowed' : 'pointer', opacity: cleaning ? 0.7 : 1 }}>
                {cleaning ? 'Cleaning...' : 'Confirm: Delete Duplicates & Restore Stock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Due;
